import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

// UX
import CardSelfServiceScheduleUX from "./CardSelfServiceScheduleUX";

// Component
import CardSelfServiceWorkSchedule from "./CardSelfServiceWorkSchedule";
import CardSelfServiceTimeAttendance from "./CardSelfServiceTimeAttendance";
import CardSelfServiceLeaveHistory from "./CardSelfServiceLeaveHistory";
import CardSelfServicePaySlip from "./CardSelfServicePaySlip";

// Common
import SnackMessage from "react-lib/apps/common/SnackMessage";
import ModInfo from "react-lib/apps/common/ModInfo";

// Interface
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  TabMenu,
  TAB_MENU_MAPPING,
} from "./sequence/SelfServiceSchedule";
import CardEmployeeTimeAttendance from "./CardEmployeeTimeAttendance";
import CardTrainingHistory from "./CardTrainingHistory";

// Type
type CardSelfServiceScheduleProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  EmployeeTASequence: any;
  SelfServiceScheduleSequence: State["SelfServiceScheduleSequence"];
  SetupWorkScheduleSequence: any;
  TrainingHistorySequence: any;
  PaySlipSequence: any;
  userProfile: any;
  masterData?: any;
  // options
  languageUX: "en" | "th";
} & PickedProps;

type Styles = Record<"timeAttendance" | "trainingHistory", CSSProperties>;

const styles: Styles = {
  timeAttendance: { margin: "-10px" },
  trainingHistory: { margin: "-10px" },
};

const CardSelfServiceSchedule = (props: CardSelfServiceScheduleProps) => {
  const intl = useIntl();
  console.log("log props: ", props);

  // useState
  const [openModInfo, setOpenMedInfo] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"" | TabMenu>("");

  const filteredTabMenu = useMemo(() => {
    const tabs = props.adminMenuDivisions?.all["Self Service & Time Schedule"]?.tabs ?? [];

    return Object.entries(TAB_MENU_MAPPING)
      .filter(([key]) => tabs.includes(key))
      .map((menu) => menu[1]);
  }, [props.adminMenuDivisions]);

  useEffect(() => {
    setActiveTab(filteredTabMenu[0] || "");
  }, [filteredTabMenu]);

  const leaveTypeList = useMemo(() => {
    return props.masterData?.leaveType || [];
  }, [props.masterData?.leaveType]);

  const leaveRemaining = useMemo(() => {
    return props.SelfServiceScheduleSequence?.leaveRemaining || {};
  }, [props.SelfServiceScheduleSequence?.leaveRemaining]);

  useEffect(() => {
    props.runSequence({ sequence: "SelfServiceSchedule", restart: true });
  }, []);

  useEffect(() => {
    setOpenMedInfo(!!props.errorMessage?.[CARD_NAME]);
  }, [props.errorMessage]);

  const handleChangeTab = (tab: TabMenu) => {
    if (tab === activeTab) return;

    setActiveTab(tab);

    props.runSequence({
      sequence: "SelfServiceSchedule",
      menu: tab,
      action: "FETCH",
    });
  };

  const handleCloseModInfo = () => {
    setOpenMedInfo(false);

    props.setProp("errorMessage", {
      ...props.errorMessage,
      [CARD_NAME]: null,
    });
  };

  const displayTab = useMemo(() => {
    switch (activeTab) {
      case "WorkSchedule":
        return (
          <CardSelfServiceWorkSchedule
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            SetupWorkScheduleSequence={props.SetupWorkScheduleSequence}
            leaveRemaining={leaveRemaining}
            // data
            leaveTypeList={leaveTypeList}
            paramKey="CardSelfServiceSchedule_WorkSchedule"
            userProfile={props.userProfile}
            buttonLoadCheck={props.buttonLoadCheck}
            errorMessage={props.errorMessage}
            selectedDivision={props.selectedDivision}
            successMessage={props.successMessage}
          />
        );
      case "TimeAttendance":
        return (
          <CardEmployeeTimeAttendance
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            EmployeeTASequence={props.EmployeeTASequence}
            readOnly
            style={styles.timeAttendance}
            buttonLoadCheck={props.buttonLoadCheck}
            searchedItemListWithKey={props.searchedItemListWithKey}
            masterOptions={props.masterOptions}
          />
        );
      case "LeaveHistory":
        return (
          <CardSelfServiceLeaveHistory
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            leaveHistory={props.SelfServiceScheduleSequence?.leaveHistory}
            buttonLoadCheck={props.buttonLoadCheck}
            // data
            leaveTypeList={leaveTypeList}
            leaveRemaining={leaveRemaining}
            paramKey="CardSelfServiceSchedule_LeaveHistory"
          />
        );
      case "TrainingHistory":
        return (
          <CardTrainingHistory
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            TrainingHistorySequence={props.TrainingHistorySequence}
            style={styles.trainingHistory}
            buttonLoadCheck={props.buttonLoadCheck}
            searchedItemListWithKey={props.searchedItemListWithKey}
            languageUX={props.languageUX}
          />
        );
      case "PaySlip":
        return (
          <CardSelfServicePaySlip
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            buttonLoadCheck={props.buttonLoadCheck}
            PaySlipSequence={props.SelfServiceScheduleSequence?.paySlip}
          />
        );
      default:
        return null;
    }
  }, [activeTab, props]);

  return (
    <>
      <ModInfo
        open={openModInfo}
        color="red"
        titleName="Error Message"
        onApprove={handleCloseModInfo}
        onClose={handleCloseModInfo}
        size="small"
      >
        {(() => {
          const error = props.errorMessage?.[CARD_NAME];

          if (typeof error === "string") return error;

          if (error && typeof error === "object") {
            const firstKey = Object.keys(error)[0];
            const content = error[firstKey];

            if (Array.isArray(content)) {
              return <div dangerouslySetInnerHTML={{ __html: content.join("<br/>") }} />;
            }
          }

          return null;
        })()}
      </ModInfo>

      <SnackMessage
        // func
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_NAME}`, null);
          props.setProp(`successMessage.${CARD_NAME}`, null);
        }}
        error={null}
        // error={props.errorMessage?.[CARD_NAME]}
        success={props.successMessage?.[CARD_NAME]}
        languageUX={props.languageUX}
      />

      <CardSelfServiceScheduleUX
        activeTab={activeTab}
        displayTab={displayTab}
        filteredTabMenu={filteredTabMenu}
        onHandleChangeTab={handleChangeTab}
      />
    </>
  );
};

export default CardSelfServiceSchedule;
